import {
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import LoadingButton from '@/components/ui/LoadingButton/LoadingButton.tsx';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import BackButton from '@/components/ui/BackButton/BackButton.tsx';
import LoaderWrapper from '@/components/ui/LoaderWrapper/LoaderWrapper.tsx';
import useFaqAdd from '@/api/useFaqAdd.ts';
import useRestaurants from '@/api/useRestaurants.ts';

export const schema = z.object({
  restaurant_id: z
    .string()
    .min(1, { message: 'This field is required.' })
    .max(50, { message: 'Must be less than 50 characters.' }),
  question: z
    .string()
    .min(1, { message: 'This field is required.' })
    .max(1500, { message: 'Must be less than 1500 characters.' }),
  answer: z
    .string()
    .min(1, { message: 'This field is required.' })
    .max(1500, { message: 'Must be less than 1500 characters.' }),
});

export type Schema = z.infer<typeof schema>;

type AddFaqModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onConfirm?: () => void;
};
function AddFaqModal({ isOpen, onClose, onConfirm }: AddFaqModalProps) {
  const { t } = useTranslation();
  const { data: restaurants, isLoading: isLoadingRestaurants } = useRestaurants();
  const {
    mutateAsync: addFaq,
    isError: isErrorAddFaq,
    isPending: isLoadingAddFaq,
    isSuccess: isSuccessAddFaq,
    error: errorAddFaq,
  } = useFaqAdd();

  const { handleSubmit, control } = useForm<Schema>({
    resolver: zodResolver(schema),
    defaultValues: {
      restaurant_id: '',
      question: '',
      answer: '',
    },
  });

  const handleAddFaq = ({ restaurant_id, ...rest }: Schema) => {
    void (async () => {
      try {
        if (isLoadingAddFaq) {
          return;
        }

        await addFaq({
          ...rest,
          restaurant_id: parseInt(restaurant_id),
        });

        if (onConfirm) {
          onConfirm();
        }

        onClose();
      } catch (error) {
        console.error('Error editing reservation: ', error);
      }
    })();
  };

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="sm" fullWidth>
      <form className="modal-form" onSubmit={handleSubmit(handleAddFaq)}>
        <DialogTitle>{t('modals.addFaqModal.title')}</DialogTitle>
        <DialogContent>
          <LoaderWrapper isLoading={isLoadingRestaurants}>
            <div className="pt-2 flex flex-col gap-4">
              <div className="flex flex-col gap-2 empty:hidden">
                {isSuccessAddFaq && <Alert severity="success">{t('modals.addFaqModal.successMessage')}</Alert>}
                {isErrorAddFaq && <Alert severity="error">{errorAddFaq?.message as string}</Alert>}
              </div>
              <div className="flex flex-col gap-4 w-full">
                <Controller
                  control={control}
                  name="restaurant_id"
                  render={({ field: { onChange, value }, formState: { errors } }) => (
                    <FormControl error={!!errors.restaurant_id}>
                      <InputLabel shrink id="restaurant_id">
                        {t('label.restaurant')}
                      </InputLabel>
                      <Select
                        value={value}
                        onChange={(event) => onChange(`${event.target.value}`)}
                        displayEmpty
                        variant="outlined"
                        label={t('label.restaurant')}
                        labelId="restaurant_id"
                        renderValue={(currentValue) =>
                          currentValue == '' ? (
                            <span className="text-[#a1a2a1]">{t('placeholder.restaurant')}</span>
                          ) : (
                            restaurants?.find((restaurant) => restaurant.id === parseInt(currentValue))?.name
                          )
                        }
                      >
                        {restaurants?.map((restaurant) => (
                          <MenuItem key={`restaurant_${restaurant.id}`} value={restaurant.id}>
                            {restaurant.name}
                          </MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>{errors.restaurant_id?.message}</FormHelperText>
                    </FormControl>
                  )}
                />
                <Controller
                  control={control}
                  name="question"
                  render={({ field: { onChange, value }, formState: { errors } }) => (
                    <TextField
                      error={!!errors.question}
                      helperText={errors.question?.message}
                      value={value}
                      onChange={onChange}
                      multiline
                      minRows={2}
                      slotProps={{
                        inputLabel: {
                          shrink: true,
                        },
                      }}
                      variant="outlined"
                      label={t('label.question')}
                      placeholder={t('placeholder.question')}
                    />
                  )}
                />
                <Controller
                  control={control}
                  name="answer"
                  render={({ field: { onChange, value }, formState: { errors } }) => (
                    <TextField
                      error={!!errors.answer}
                      helperText={errors.answer?.message}
                      value={value}
                      onChange={onChange}
                      multiline
                      minRows={4}
                      slotProps={{
                        inputLabel: {
                          shrink: true,
                        },
                      }}
                      variant="outlined"
                      label={t('label.answer')}
                      placeholder={t('placeholder.answer')}
                    />
                  )}
                />
              </div>
            </div>
          </LoaderWrapper>
        </DialogContent>
        <DialogActions>
          <div className="flex flex-wrap grow p-4 pt-0 justify-between">
            <BackButton onClick={onClose} autoFocus />
            <LoadingButton color="primary" variant="contained" loading={isLoadingAddFaq} type="submit">
              {t('button.save')}
            </LoadingButton>
          </div>
        </DialogActions>
      </form>
    </Dialog>
  );
}

export default AddFaqModal;
